import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/chadcn/Tooltip";
import BloodGlucoseClassification from "@/model/BloodGlucoseClassification";
import { BloodGlucoseObservation } from "@/model/BloodGlucoseObservation";
import cn from "@/util/cn";
import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import { Locale } from "@js-joda/locale";
import { sortBy } from "lodash";
import groupBy from "lodash/groupBy";
import { ComponentPropsWithoutRef, Fragment } from "react";
import { titleForContext, titleForQuickCheckReason } from "./Helpers";

type SidePanelProps = {
  observations?: BloodGlucoseObservation[];
  selectedObservation?: BloodGlucoseObservation;
} & ComponentPropsWithoutRef<"div">;

const SidePanel = ({
  observations,
  selectedObservation,
  className,
  ...otherProps
}: SidePanelProps) => {
  if (!observations) {
    return (
      <div
        className={cn(
          "flex items-center justify-center px-4 text-center text-sm font-light text-primary/40",
          className
        )}
        {...otherProps}
      >
        <div>Select a measurement from the table for details</div>
      </div>
    );
  } else if (observations.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center px-4 text-center text-sm font-light text-primary/40",
          className
        )}
        {...otherProps}
      >
        No measurements or food intake logged by the patient for the selected moment
      </div>
    );
  } else {
    const observationsByDate = groupBy(observations, (observation) =>
      observation.dateTime.toLocalDate().toString()
    );
    const sortedDates = sortBy(Object.keys(observationsByDate));

    return (
      <div className={cn("flex flex-col pb-4", className)}>
        {sortedDates.map((date, dateIndex) => (
          <section className="flex flex-col" key={`section-${date}`}>
            <div className="sticky top-0">
              <h1 className="truncate bg-gray-50 px-4 pb-2 pt-4 text-2xl text-primary/90">
                {dateFormatter.format(LocalDate.parse(date))}
              </h1>
              <div className="relative">
                <div className="absolute h-2 w-full bg-linear-to-b from-gray-50 to-transparent" />
              </div>
            </div>
            <div className="flex flex-col gap-8">
              {observationsByDate[date].map((observation, observationIndex) => {
                const { reading, form } = observation;
                return (
                  <Fragment key={`observation-${observation.id}}`}>
                    <dl
                      data-id={observation.id}
                      className={cn(
                        "grid scroll-m-16 grid-cols-[1fr_1fr] gap-x-2",
                        observation.id === selectedObservation?.id
                          ? "border-l-4 border-blue-300/80 pl-3 pr-4"
                          : "px-4"
                      )}
                    >
                      {reading && (
                        <>
                          <SidePanelItem
                            title={"Time"}
                            value={timeFormatter.format(reading.dateTime)}
                          />
                          <dt className="truncate text-primary/60">Measurement:</dt>
                          <dd
                            className={cn(
                              "font-extrabold text-primary/90",
                              textColorForClassification(reading.classification)
                            )}
                          >{`${reading.bloodGlucoseInMilligramsPerDeciliter} mg/dL`}</dd>
                        </>
                      )}
                      {form && (
                        <>
                          <SidePanelItem
                            title="Context"
                            value={
                              <div className="flex">
                                <span className="pe-2">{titleForContext(form.context)}</span>
                                <TooltipProvider delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger className="flex items-center">
                                      <span
                                        className="material-icons-outlined"
                                        style={{ fontSize: "18px" }}
                                      >
                                        description
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="select-none text-base shadow-lg ring-1 ring-slate-200">
                                      <p className="text-primary/60">Form submitted at</p>
                                      <p className="font-semibold text-primary/90">
                                        {dateTimeFormatter.format(form.dateTime)}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            }
                          />
                          {form.medications && (
                            <SidePanelItem
                              title="Medication"
                              value={
                                <div className="flex flex-col">
                                  <span>
                                    {form.medications.medicationTaken === "YES" ? "Yes" : "No"}
                                  </span>
                                  {form.medications.note && (
                                    <span>
                                      <span className="font-bold">Note:</span>{" "}
                                      {form.medications.note}
                                    </span>
                                  )}
                                </div>
                              }
                            />
                          )}
                          {form.mealContents !== undefined && (
                            <SidePanelItem title="Meal contents" value={form.mealContents} />
                          )}
                          {form.carbsInGrams !== undefined && (
                            <SidePanelItem title="Carbs" value={`${form.carbsInGrams} grams`} />
                          )}
                          {form.proteinInGrams !== undefined && (
                            <SidePanelItem title="Protein" value={`${form.proteinInGrams} grams`} />
                          )}
                          {form.reasons !== undefined && (
                            <SidePanelItem
                              title="Reason"
                              value={`${form.reasons.map((reason) => titleForQuickCheckReason(reason)).join(", ")}`}
                            />
                          )}
                          {form.notes !== undefined && (
                            <SidePanelItem title="Notes" value={`${form.notes}`} />
                          )}
                        </>
                      )}
                    </dl>
                    {!(
                      dateIndex === sortedDates.length - 1 &&
                      observationIndex === observationsByDate[date].length - 1
                    ) && (
                      <div className="flex w-full">
                        <div
                          className={cn(
                            "h-px grow border-t border-gray-500/15",
                            observationIndex !== observationsByDate[date].length - 1 ? "mx-4" : ""
                          )}
                        />
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    );
  }
};

const textColorForClassification = (classification: BloodGlucoseClassification) => {
  switch (classification) {
    case "critical-low":
    case "elevated":
    case "low":
      return "text-measurement-text-warning";
    case "emergency-low":
    case "critical-high":
      return "text-measurement-text-emergency";
    default:
      return "text-primary/90";
  }
};

type SidePanelItemProps = {
  title: string;
  value: React.ReactNode;
};

const SidePanelItem = ({ title, value }: SidePanelItemProps) => (
  <>
    <dt className="truncate text-primary/60">{`${title}:`}</dt>
    <dd className="text-primary/90">{value}</dd>
  </>
);

const timeFormatter = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US);
const dateFormatter = DateTimeFormatter.ofPattern("MMMM d yyyy").withLocale(Locale.US);
const dateTimeFormatter = DateTimeFormatter.ofPattern("MMMM d 'at' h:mm a").withLocale(Locale.US);

export default SidePanel;
